import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PersoonRow } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { Eyebrow } from '../../components/ui/Eyebrow';
import { ZoekBalk } from '../../components/ui/ZoekBalk';
import { useArchivedPersonen, useDearchiveerPersoon } from './personen.api';

const COLS = '1fr 1fr 120px 140px';

function ArchiefRow({ p }: { p: PersoonRow }) {
  const navigate = useNavigate();
  const dearchiveer = useDearchiveerPersoon(p.id);
  return (
    <div
      onClick={() => navigate(`/personen/${p.id}`)}
      style={{
        display: 'grid',
        gridTemplateColumns: COLS,
        gap: 10,
        padding: '12px 20px',
        borderBottom: '1px solid #F0E8DC',
        fontSize: 13,
        alignItems: 'center',
        cursor: 'pointer',
        minWidth: 620,
      }}
    >
      <span style={{ fontWeight: 700, color: '#2A6085' }}>{p.naam}</span>
      <span style={{ color: '#666666' }}>{p.email || '—'}</span>
      <span>
        <Badge tone="neutral">Uit dienst</Badge>
      </span>
      <span style={{ textAlign: 'right' }}>
        <Button
          variant="secondary"
          size="sm"
          disabled={dearchiveer.isPending}
          onClick={(e) => {
            e.stopPropagation();
            dearchiveer.mutate();
          }}
        >
          {dearchiveer.isPending ? 'Bezig…' : 'Terug in dienst'}
        </Button>
      </span>
    </div>
  );
}

export function PersonenArchiefPage() {
  const navigate = useNavigate();
  const { data: personen, isLoading } = useArchivedPersonen();
  const [q, setQ] = useState('');

  const filtered = useMemo(() => {
    const all = personen ?? [];
    const term = q.trim().toLowerCase();
    if (!term) return all;
    return all.filter((p) => `${p.naam} ${p.email ?? ''}`.toLowerCase().includes(term));
  }, [personen, q]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/personen')}>
            ← Alle personen
          </Button>
          <div style={{ marginTop: 8 }}>
            <Eyebrow>Archief</Eyebrow>
            <h2 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 900, color: '#111111' }}>
              Personen uit dienst.
            </h2>
          </div>
        </div>
      </div>

      <ZoekBalk
        waarde={q}
        onWaarde={setQ}
        placeholder="Zoek op naam of e-mail…"
        ariaLabel="Archief zoeken"
        aantal={
          filtered.length === (personen?.length ?? 0)
            ? `${filtered.length} personen`
            : `${filtered.length} van ${personen?.length ?? 0}`
        }
      />

      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E3D9CC',
          borderRadius: 4,
          overflowX: 'auto',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            gap: 10,
            padding: '12px 20px',
            borderBottom: '2px solid #111111',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: '#666666',
            minWidth: 620,
          }}
        >
          <span>Naam</span>
          <span>E-mail</span>
          <span>Status</span>
          <span style={{ textAlign: 'right' }}>Actie</span>
        </div>
        {isLoading && <div style={{ padding: '24px 20px', color: '#888888' }}>Archief laden…</div>}
        {filtered.map((p) => (
          <ArchiefRow key={p.id} p={p} />
        ))}
        {!isLoading && filtered.length === 0 && (
          <div style={{ padding: '24px 20px', color: '#888888' }}>
            {q ? 'Geen personen gevonden.' : 'Geen personen uit dienst.'}
          </div>
        )}
      </div>
    </div>
  );
}
